import React, { useState } from "react";
import { TreeSelect, Switch, Modal } from "antd";
import Icon from "@ant-design/icons";
import { ArrowDownSvg, CloseSvg } from "../../../assets/icons";
import { InviteModalTreeSelector } from "../../../styles/UserManagement.styled";
import { MODULE_SCOPES, emptyProductScopes } from "../../../constants/moduleScopes";
import { buildTreeData } from "./scopeTreeData";

const { SHOW_PARENT } = TreeSelect;

const ArrowDownIcon = (props) => <Icon component={ArrowDownSvg} {...props} />;

// Tag render for perfect vertical centering of selected-market chips.
const optimizedTagRender = (props) => {
  const { label, closable, onClose } = props;
  return (
    <div
      className="flex items-center mr-1 px-2 rounded-full border border-[#00000010] text-[0.75rem] text-[#0e49b5] bg-[#f7f9fc]"
      style={{ height: "1.5rem" }}
    >
      <div className="h-full flex items-center">{label}</div>
      {closable && (
        <div
          className="flex items-center pl-1 cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
        >
          <Icon component={CloseSvg} />
        </div>
      )}
    </div>
  );
};

/**
 * Workspace-agnostic product-scope editor. Controlled: the parent owns the
 * `productScopes` map and the already-clamped `markets` list; this component
 * owns the per-module vs. Admin "All Products" rendering, the per-row select-all
 * affordance, and emits the whole next scope map via `onChange`.
 *
 * Role semantics (the shared role model — backend role values):
 * - `"super_admin"` → renders nothing (full access, no per-module scope).
 * - `"admin"` → collapses to a single "All Products" row whose selection is
 *   written to every module.
 * - anything else → one checkable-tree row per module.
 *
 * @param {object} props
 * @param {string} props.role
 * @param {string[]} props.markets
 * @param {Record<string, string[]>} props.productScopes
 * @param {(next: Record<string, string[]>) => void} props.onChange
 * @param {boolean} [props.showLabel] Render the built-in "Scope" label (default
 *   true). Pass false when the caller supplies its own label around the picker.
 * @param {boolean} [props.disabled] Read-only mode (default false) — disables the
 *   market pickers and the select-all toggles.
 */
export default function ScopePicker({
  role,
  markets = [],
  productScopes = {},
  onChange,
  showLabel = true,
  disabled = false,
}) {
  // Non-Admin "apply the same markets to every product" toggle. Local UI state
  // (declared before the early return to satisfy the rules-of-hooks); Admin and
  // super_admin ignore it — they're already all-products.
  const [applyAll, setApplyAll] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (role === "super_admin") return null;

  const isAdmin = role === "admin";
  const treeData = buildTreeData(markets);

  // Representative selection for the Admin single-row picker — the union of every
  // module's list (kept identical for the Admin role).
  const allSelectedMarkets = Array.from(new Set(Object.values(productScopes).flat()));

  const setModule = (moduleKey, values) =>
    onChange({ ...productScopes, [moduleKey]: values || [] });

  const setAllProducts = (values) => {
    const list = values || [];
    const next = emptyProductScopes();
    MODULE_SCOPES.forEach((module) => {
      next[module.key] = [...list];
    });
    onChange(next);
  };

  const toggleModuleSelectAll = (moduleKey) =>
    setModule(moduleKey, productScopes[moduleKey]?.length === markets.length ? [] : [...markets]);

  const toggleAllProductsSelectAll = () => {
    const allSelected = markets.length > 0 && allSelectedMarkets.length === markets.length;
    setAllProducts(allSelected ? [] : markets);
  };

  // Do the modules currently hold different market sets?
  const sameSet = (a, b) => {
    const sa = new Set(a);
    return a.length === b.length && b.every((x) => sa.has(x));
  };
  const moduleScopeLists = MODULE_SCOPES.map((m) => productScopes[m.key] || []);
  const modulesDiffer = moduleScopeLists.some((list) => !sameSet(list, moduleScopeLists[0]));

  const enableApplyAll = () => {
    setAllProducts(allSelectedMarkets); // seed with the union, losing no market
    setApplyAll(true);
  };

  const handleToggleApplyAll = (checked) => {
    if (!checked) {
      // Back to per-product editing — modules are unified at this point, so
      // nothing is lost and there's nothing to confirm.
      setApplyAll(false);
      return;
    }
    // Enabling flattens every product to one shared set. If the products
    // already differ, that overwrites their individual markets — confirm first.
    if (modulesDiffer) {
      setConfirmOpen(true);
      return;
    }
    enableApplyAll();
  };

  // Admin is always collapsed; a non-Admin collapses only when the toggle is on.
  const collapsed = isAdmin || applyAll;
  const rows = collapsed ? [{ key: "__all_products__", label: "All Products" }] : MODULE_SCOPES;

  return (
    <div className="flex flex-col gap-3">
      {showLabel && <label className="text-gray-700 text-sm font-bold font-['Inter']">Scope</label>}
      {!isAdmin && (
        <div className="flex items-center gap-2">
          <Switch
            size="small"
            checked={applyAll}
            onChange={handleToggleApplyAll}
            disabled={disabled || markets.length === 0}
          />
          <span className="text-xs text-gray-600 font-['Inter']">
            Apply the same markets to all products
          </span>
        </div>
      )}
      <div className="flex flex-col gap-3">
        {rows.map((module) => {
          const isAllProducts = module.key === "__all_products__";
          const selected = isAllProducts ? allSelectedMarkets : productScopes[module.key] || [];
          const allSelected = markets.length > 0 && selected.length === markets.length;
          return (
            <div
              key={module.key}
              className={`flex gap-3 ${isAllProducts ? "items-center" : "items-start"}`}
            >
              <div className={`w-48 flex flex-col ${isAllProducts ? "gap-1.5" : ""}`}>
                <div
                  className={`text-gray-700 font-medium font-['Inter'] ${
                    isAllProducts ? "text-sm" : "text-xs"
                  }`}
                >
                  {module.label}
                </div>
                <button
                  type="button"
                  onClick={() =>
                    isAllProducts ? toggleAllProductsSelectAll() : toggleModuleSelectAll(module.key)
                  }
                  disabled={disabled || markets.length === 0}
                  className="text-left text-[#0e49b5] text-xs font-normal font-['Inter'] hover:underline disabled:text-gray-300 disabled:no-underline disabled:cursor-not-allowed"
                >
                  {allSelected ? "Clear all" : "Select all markets"}
                </button>
              </div>
              <div className="flex-1">
                <InviteModalTreeSelector
                  className="invite-modal-tree-selector"
                  treeData={treeData}
                  value={selected}
                  disabled={disabled}
                  onChange={(values) =>
                    isAllProducts ? setAllProducts(values) : setModule(module.key, values)
                  }
                  placeholder="Choose markets"
                  showSearch
                  style={{ width: "100%", borderRadius: "8px", height: "40px" }}
                  styles={{
                    popup: {
                      root: {
                        maxHeight: 200,
                        overflow: "auto",
                        borderRadius: "12px",
                        padding: "0.75rem",
                        boxShadow:
                          "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                      },
                    },
                  }}
                  treeDefaultExpandAll
                  suffixIcon={<ArrowDownIcon />}
                  classNames={{ popup: { root: "rounded-lg shadow-lg" } }}
                  treeCheckable={true}
                  showCheckedStrategy={SHOW_PARENT}
                  maxTagCount={2}
                  maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} more`}
                  allowClear
                  tagRender={optimizedTagRender}
                />
              </div>
            </div>
          );
        })}
      </div>

      <Modal
        open={confirmOpen}
        title="Apply one market set to all products?"
        okText="Apply to all"
        cancelText="Cancel"
        onOk={() => {
          enableApplyAll();
          setConfirmOpen(false);
        }}
        onCancel={() => setConfirmOpen(false)}
      >
        These products currently have different markets. Turning this on will overwrite each
        product&apos;s markets with a single shared set (the union of what&apos;s selected now).
        This can&apos;t be auto-undone.
      </Modal>
    </div>
  );
}
